import { ExclamationCircleFilled } from '@ant-design/icons';

import ModalFooter from '@/UI/ModalFooter';

import type { ButtonProps } from 'antd';
import type { HookAPI } from 'antd/es/modal/useModal';
import type { ReactNode } from 'react';

type ConfirmDestructiveOptions = {
  /** Short question ("Discard unsaved changes?") — the sentence goes in `content`. */
  title: ReactNode;
  /** Optional dialog body content. */
  content?: ReactNode;
  /** Optional custom icon. Defaults to a red exclamation matching the danger action. */
  icon?: ReactNode;
  /** OK button label. */
  okText?: ReactNode;
  /** Cancel button label. */
  cancelText?: ReactNode;
  /** Called when the destructive action is confirmed. */
  onOk: () => void | Promise<void>;
  /** Called when the dialog is dismissed. */
  onCancel?: () => void;
  /** Overrides merged on top of the destructive `{ danger: true }` default. */
  okButtonProps?: ButtonProps;
  /** Props applied to the cancel button. */
  cancelButtonProps?: ButtonProps;
};

/**
 * Opens a standardized destructive confirmation dialog via antd's imperative
 * modal API. The `modal` instance must come from `App.useApp()` so the dialog
 * inherits the app's themed ConfigProvider context (required in antd v6).
 *
 * Standardizes the destructive confirm anatomy: red exclamation icon, danger
 * OK button, and both buttons bottom-right (Cancel first, destructive action
 * in the primary position) — matching the app-wide footer convention.
 */
export function confirmDestructive(
  modal: HookAPI,
  { okButtonProps, ...options }: ConfirmDestructiveOptions,
) {
  return modal.confirm({
    icon: (
      <ExclamationCircleFilled style={{ color: 'var(--ant-color-error)' }} />
    ),
    ...options,
    okButtonProps: { danger: true, ...okButtonProps },
    footer: (_originNode, { OkBtn, CancelBtn }) => (
      <ModalFooter
        actions={
          <>
            <CancelBtn />
            <OkBtn />
          </>
        }
      />
    ),
  });
}
